import { FormGroup, Validators } from '@angular/forms';
import { CustomValidators } from './custom-validators';

/**
 * @constructor argument path to controls
 * @example
 * class ... extends LinieValidationManager ...
 * constructor(...) {
 *  super({ start: 'von', end: 'bis' });
 *  this.form = this.formGroup; // Das Reference vom formGroup zu LinieValidators Class übertragen
 * }
 * this.direction = this.initLinieValidators(this.startmeter, this.endmeter);
 */
export abstract class LinieValidationManager {
  private _min: number;
  private _max: number;
  private _direction: 1 | -1;

  constructor(
    private path?: { start: string; end: string },
    private allowZero = false
  ) {
    this.path ??= { start: 'Startmeter', end: 'Endmeter' };
  }
  public form: FormGroup;
  /**
   * @argument required - adds Validators.required to Start&End oder MindestensEinWertValidator to form
   * @see CustomValidators.MindestensEinWertValidator
   * @see CustomValidators.DirectionValidator
   * @argument f - falls `this.form = this.%formGroupName%` nicht gemacht wurde
   * @returns direction = start < end ? 1 : -1
   */
  initLinieValidators(start: number, end: number, required: boolean = true, f?: FormGroup): 1 | -1 {
    this.form ??= f;
    this.init(start, end);

    const validators = [Validators.min(this._min), Validators.max(this._max)];
    if (required) validators.push(Validators.required);

    const startCtrl = this.form.get(this.path.start);
    const endCtrl = this.form.get(this.path.end);
    (startCtrl as any)._updateOn = 'blur';
    (endCtrl as any)._updateOn = 'blur';
    startCtrl.setValidators(validators);
    endCtrl.setValidators(validators);

    this.form.setValidators(CustomValidators.DirectionValidator(this._direction, this.path, this.allowZero));
    if (!required)
      this.form.addValidators(CustomValidators.MindestensEinWertValidator([this.path.start, this.path.end]));
    return this._direction;
  }

  private init(start: number, end: number) {
    this._min = Math.min(start, end);
    this._max = Math.max(start, end);
    this._direction = calcDirection(start, end);
  }

  clearLinieValidators(form?: FormGroup) {
    this.form ??= form;
    this.form.get(this.path.start).clearValidators();
    this.form.get(this.path.end).clearValidators();
    this.form.clearValidators();
    this.form.get(this.path.start).updateValueAndValidity({ onlySelf: true });
    this.form.get(this.path.end).updateValueAndValidity();
  }

  isLinieValid(rows: object[], start: number, end: number) {
    this.init(start, end);
    return rows.every(row => {
      const von = row[this.path.start];
      const bis = row[this.path.end];
      if (typeof von != 'number') return false;
      if (typeof bis != 'number') return false;

      if (von < this._min || bis < this._min || von > this._max || bis > this._max) return false;

      if (von == bis) return this.allowZero;

      switch (this._direction) {
        case 1:
          return von < bis;
        case -1:
          return bis < von;
      }
    });
  }
}

export function calcDirection(start: number, end: number) {
  return start < end ? 1 : -1;
}
